import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { projectsApi, knowledgeBasesApi } from '../utils/api';
import type { KnowledgeBaseListItem } from '../types/knowledgeBase';
import Navigation from '../components/Navigation';

export default function NewProjectPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedKBs, setSelectedKBs] = useState<number[]>([]);
  const [availableKBs, setAvailableKBs] = useState<KnowledgeBaseListItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadKBs = async () => {
      try {
        const kbs = await knowledgeBasesApi.getAll(false);
        setAvailableKBs(kbs);
      } catch (err) {
        console.error('Failed to load knowledge bases:', err);
      }
    };
    loadKBs();
  }, []);

  const handleKBToggle = (kbId: number) => {
    setSelectedKBs(prev =>
      prev.includes(kbId)
        ? prev.filter(id => id !== kbId)
        : [...prev, kbId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      const project = await projectsApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        knowledge_base_ids: selectedKBs.length > 0 ? selectedKBs : undefined,
      });
      navigate(`/projects/${project.id}`);
    } catch (err) {
      setError('Failed to create project');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Project - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

      <div className="max-w-3xl mx-auto px-4 pt-24 pb-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Create Project</h1>
          <p className="text-gray-600 mt-2">
            Create a new project to organize tasks and knowledge bases
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Project Name *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
                placeholder="Enter project name"
                required
              />
            </div>

            <div className="mb-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
                placeholder="Describe this project (optional)"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Knowledge Bases
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select knowledge bases to associate with this project. All tasks in this project will have access to these knowledge bases.
              </p>
              {availableKBs.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No knowledge bases available</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {availableKBs.map(kb => (
                    <label key={kb.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={selectedKBs.includes(kb.id)}
                        onChange={() => handleKBToggle(kb.id)}
                        className="rounded border-gray-300"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium">{kb.title}</span>
                        {kb.description && (
                          <p className="text-xs text-gray-500 line-clamp-1">{kb.description}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">{kb.resource_count} resources</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 btn-primary rounded-md text-sm font-medium disabled:opacity-50"
              >
                {isSubmitting ? 'Creating...' : 'Create Project'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/projects')}
                className="px-6 py-2 btn-secondary rounded-md text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            💡 After creating the project, you can create tasks within it and manage the associated knowledge bases.
          </p>
        </div>
      </div>
    </div>
    </>
  );
}
